import { useEffect, useRef } from "react";
import { computeRms } from "../../audio/rms";

// マイク入力レベルの可視化 (spec §4.4 Step 2)。VadDebugOverlay の RMS 実装から抽出。
// 「話すとバーが動く」ことでマイク疎通を体感させる Preflight 本番 UI。
type Props = {
  stream: MediaStream | null;
  width?: number;
  height?: number;
  className?: string;
};

export function MicLevelMeter({ stream, width = 280, height = 16, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!stream) return;
    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const buffer = new Float32Array(analyser.frequencyBinCount);
    let raf = 0;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    const draw = () => {
      if (!canvas || !ctx) return;
      analyser.getFloatTimeDomainData(buffer);
      const rms = computeRms(buffer);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#22c55e";
      const barWidth = Math.min(canvas.width, rms * canvas.width * 4);
      ctx.fillRect(0, 0, barWidth, canvas.height);
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      source.disconnect();
      void audioCtx.close();
    };
  }, [stream]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      role="img"
      aria-label="マイク入力レベル"
      className={className ?? "block w-full rounded bg-slate-200"}
    />
  );
}
